import { DialogComponent } from '@theme/dialog';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { clamp } from '@theme/utilities';

/**
 * A clean fullscreen product image lightbox (navigation only — no zoom/pan).
 *
 * Markup lives in snippets/product-media-gallery-content.liquid. Extends DialogComponent for
 * open/close, scroll-lock, Escape and click-outside. The carousel is a native horizontal
 * scroll-snap track (reliable swipe/trackpad); arrows/thumbnails scroll the active slide into view,
 * and a scroll listener derives the active index (each slide is full-width) to drive the counter +
 * active thumbnail.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element (required by DialogComponent).
 * @property {HTMLElement} [track] - The horizontal scroll-snap track.
 * @property {HTMLElement[]} [slides] - The slide elements.
 * @property {HTMLElement[]} [thumbnails] - The thumbnail buttons.
 * @property {HTMLElement} [counter] - The "n / total" counter.
 *
 * @extends DialogComponent
 */
class ProductLightbox extends DialogComponent {
	/** @type {number} */
	#index = 0;

	connectedCallback() {
		super.connectedCallback();
		const track = this.refs.track;
		if (track instanceof HTMLElement) track.addEventListener('scroll', this.#onScroll, { passive: true });
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		const track = this.refs.track;
		if (track instanceof HTMLElement) track.removeEventListener('scroll', this.#onScroll);
	}

	/**
	 * Open the lightbox at a given media index.
	 * @param {number} index
	 * @param {Event} [event]
	 */
	open(index, event) {
		if (event instanceof Event) event.preventDefault();
		this.showDialog();
		const target = clamp(Number(index) || 0, 0, Math.max(0, this.#slides.length - 1));
		// Wait for the dialog to be laid out before measuring/scrolling the track.
		requestAnimationFrame(() => {
			this.#scrollTo(target, 'instant');
			this.#setActive(target);
		});
	}

	next() {
		this.#scrollTo(this.#index + 1, 'smooth');
	}

	previous() {
		this.#scrollTo(this.#index - 1, 'smooth');
	}

	/** @param {number} index */
	goto(index) {
		this.#scrollTo(Number(index) || 0, 'smooth');
	}

	/** @param {KeyboardEvent} event */
	handleKeys(event) {
		if (event.key === 'ArrowRight') {
			event.preventDefault();
			this.next();
		} else if (event.key === 'ArrowLeft') {
			event.preventDefault();
			this.previous();
		}
	}

	/**
	 * Clicking the dark letterbox area (anything that isn't the photo) closes the lightbox.
	 * @param {Event} event
	 */
	handleSlideClick(event) {
		if (!(event.target instanceof HTMLImageElement)) this.closeDialog();
	}

	/** @returns {Element[]} */
	get #slides() {
		const slides = this.refs.slides;
		return Array.isArray(slides) ? slides : [];
	}

	/** @returns {Element[]} */
	get #thumbnails() {
		const thumbs = this.refs.thumbnails;
		return Array.isArray(thumbs) ? thumbs : [];
	}

	/**
	 * @param {number} index
	 * @param {ScrollBehavior} behavior
	 */
	#scrollTo(index, behavior) {
		const track = this.refs.track;
		if (!(track instanceof HTMLElement)) return;
		const target = clamp(index, 0, Math.max(0, this.#slides.length - 1));
		track.scrollTo({ left: target * track.clientWidth, behavior });
	}

	#onScroll = () => {
		const track = this.refs.track;
		if (!(track instanceof HTMLElement) || !track.clientWidth) return;
		const index = Math.round(track.scrollLeft / track.clientWidth);
		if (index !== this.#index) this.#setActive(index);
	};

	/** @param {number} index */
	#setActive(index) {
		const slides = this.#slides;
		if (slides.length === 0) return;
		this.#index = clamp(index, 0, slides.length - 1);

		const counter = this.refs.counter;
		if (counter instanceof HTMLElement) counter.textContent = `${this.#index + 1} / ${slides.length}`;

		this.#thumbnails.forEach((thumb, i) => {
			const active = i === this.#index;
			thumb.classList.toggle('mukua-lightbox__thumb--active', active);
			thumb.setAttribute('aria-current', active ? 'true' : 'false');
			if (active) thumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
		});

		// Let the inline gallery follow along (media-gallery.js listens for this bubbling event).
		this.dispatchEvent(new ZoomMediaSelectedEvent(this.#index));
	}
}

if (!customElements.get('product-lightbox')) {
	customElements.define('product-lightbox', ProductLightbox);
}
